import { AnimaliaUseCases } from '../../application/animaliaUseCasesFactory';
import { AnimaliaDetailPageDTO } from '../../application/mappers/AnimaliaDetailMapper';
import {
  animaliaListFiltersFromQuery,
  animaliaListFiltersToQueryRecord,
} from '../../application/taxonomy/AnimaliaNavigableTaxonomy';
import { buildRoute, getCurrentLocation } from '../../../../shared/ui/router';
import {
  applySeoMetadata,
  buildSiteUrl,
  createWebsiteJsonLd,
} from '../../../../shared/ui/seo/seoMetadata';
import '../components/detail/hero/mv-animalia-detail-hero';
import '../components/detail/fields/mv-animalia-detail-fields';
import '../components/detail/taxonomy/mv-animalia-detail-taxonomy';
import '../components/detail/narrative/mv-animalia-detail-narrative';
import '../components/detail/status/mv-animalia-detail-status';
import type { MvAnimaliaDetailHero } from '../components/detail/hero/mv-animalia-detail-hero';
import type { MvAnimaliaDetailFields } from '../components/detail/fields/mv-animalia-detail-fields';
import type { MvAnimaliaDetailTaxonomy } from '../components/detail/taxonomy/mv-animalia-detail-taxonomy';
import type { MvAnimaliaDetailNarrative } from '../components/detail/narrative/mv-animalia-detail-narrative';
import type { MvAnimaliaDetailStatus } from '../components/detail/status/mv-animalia-detail-status';

type ViewState = 'idle' | 'loading' | 'ready' | 'not-found' | 'error';

export class MvAnimaliaDetailPage extends HTMLElement {
  private animaliaUseCases!: AnimaliaUseCases;

  private slug = '';

  private detail: AnimaliaDetailPageDTO | null = null;

  private viewState: ViewState = 'idle';

  private errorMessage = '';

  private readonly handleRetryRequested = () => {
    void this.loadDetail();
  };

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
  }

  setUseCases(animaliaUseCases: AnimaliaUseCases): void {
    this.animaliaUseCases = animaliaUseCases;
  }

  setSlug(slug: string): void {
    if (this.slug === slug) {
      return;
    }

    this.slug = slug;

    if (this.isConnected) {
      void this.loadDetail();
    }
  }

  connectedCallback(): void {
    this.render();
    this.addEventListeners();

    if (this.viewState === 'idle' && this.slug !== '') {
      void this.loadDetail();
    }
  }

  disconnectedCallback(): void {
    this.removeEventListeners();
  }

  private addEventListeners(): void {
    this.shadowRoot?.addEventListener('retry-requested', this.handleRetryRequested as EventListener);
  }

  private removeEventListeners(): void {
    this.shadowRoot?.removeEventListener('retry-requested', this.handleRetryRequested as EventListener);
  }

  private async loadDetail(): Promise<void> {
    if (!this.animaliaUseCases) {
      throw new Error('Animalia use cases have not been provided.');
    }

    if (this.slug.trim() === '') {
      this.viewState = 'not-found';
      this.detail = null;
      this.render();
      return;
    }

    this.viewState = 'loading';
    this.errorMessage = '';
    this.render();

    try {
      const result = await this.animaliaUseCases.fetchAnimaliaDetail.execute(this.slug);

      if (result.status === 'not-found') {
        this.detail = null;
        this.viewState = 'not-found';
      } else {
        this.detail = result.detail;
        this.viewState = 'ready';
      }
    } catch (error: unknown) {
      this.detail = null;
      this.viewState = 'error';
      this.errorMessage = error instanceof Error ? error.message : 'No se pudo recuperar la ficha.';
      console.error('Animalia detail failed to load.', error);
    }

    this.applySeo();
    this.render();
    this.addEventListeners();
  }

  private render(): void {
    if (!this.shadowRoot) {
      return;
    }

    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: block;
        }

        * {
          box-sizing: border-box;
        }

        .page-shell {
          display: grid;
          gap: 30px;
        }

        .initial-loading {
          display: grid;
          gap: 28px;
          padding: 10px 0 4px;
        }

        .initial-loading-block {
          min-height: 120px;
          border-radius: 28px;
          background:
            linear-gradient(90deg, rgba(49, 70, 61, 0.06), rgba(49, 70, 61, 0.12), rgba(49, 70, 61, 0.06));
          background-size: 200% 100%;
          animation: placeholder-shimmer 1.4s ease-in-out infinite;
        }

        .initial-loading-layout {
          display: grid;
          grid-template-columns: minmax(0, 1.08fr) minmax(360px, 0.92fr);
          gap: 28px;
        }

        .initial-loading-column {
          display: grid;
          gap: 28px;
        }

        .initial-loading-card {
          min-height: 240px;
          border-radius: 28px;
          background:
            linear-gradient(90deg, rgba(49, 70, 61, 0.06), rgba(49, 70, 61, 0.12), rgba(49, 70, 61, 0.06));
          background-size: 200% 100%;
          animation: placeholder-shimmer 1.4s ease-in-out infinite;
        }

        @keyframes placeholder-shimmer {
          0% {
            background-position: 200% 0;
          }

          100% {
            background-position: -200% 0;
          }
        }

        .detail-layout {
          display: grid;
          grid-template-columns: minmax(0, 1.08fr) minmax(360px, 0.92fr);
          gap: 28px;
          align-items: start;
        }

        .column {
          display: grid;
          gap: 28px;
        }

        .page-footer {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          align-items: center;
          gap: 8px 14px;
          padding: 18px 12px 6px;
          border-top: 1px solid rgba(49, 70, 61, 0.12);
          color: rgba(49, 70, 61, 0.72);
          font-size: 0.78rem;
          letter-spacing: 0.03em;
        }

        .footer-separator {
          color: rgba(49, 70, 61, 0.35);
        }

        .footer-link {
          color: #31463d;
          text-decoration: none;
        }

        .footer-link:hover,
        .footer-link:focus-visible {
          text-decoration: underline;
          text-underline-offset: 0.18em;
        }

        @media (max-width: 980px) {
          .initial-loading-layout,
          .detail-layout {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 640px) {
          .page-footer {
            flex-direction: column;
          }

          .footer-separator {
            display: none;
          }
        }
      </style>

      <main class="page-shell" data-state="${this.viewState}">
        ${this.renderContent()}
        ${this.renderFooter()}
      </main>
    `;

    this.attachChildren();
  }

  private renderContent(): string {
    if (this.isInitialLoadingState()) {
      return this.renderInitialLoadingPlaceholder();
    }

    if (this.viewState === 'loading') {
      return '<mv-animalia-detail-status></mv-animalia-detail-status>';
    }

    if (this.viewState === 'not-found') {
      return '<mv-animalia-detail-status></mv-animalia-detail-status>';
    }

    if (this.viewState === 'error') {
      return '<mv-animalia-detail-status></mv-animalia-detail-status>';
    }

    if (!this.detail) {
      return '';
    }

    return `
      <mv-animalia-detail-hero></mv-animalia-detail-hero>
      <section class="detail-layout">
        <div class="column">
          <mv-animalia-detail-fields data-section="identity"></mv-animalia-detail-fields>
          <mv-animalia-detail-fields data-section="references"></mv-animalia-detail-fields>
        </div>
        <div class="column">
          <mv-animalia-detail-taxonomy></mv-animalia-detail-taxonomy>
        </div>
      </section>
      <mv-animalia-detail-narrative></mv-animalia-detail-narrative>
    `;
  }

  private renderFooter(): string {
    if (this.isInitialLoadingState()) {
      return '';
    }

    return `
      <footer class="page-footer" aria-label="Informacion de contacto y autoria">
        <span>Desarrollado por <a class="footer-link" href="https://x.com/nandawtek" target="_blank" rel="noreferrer">@nandawtek</a></span>
        <span class="footer-separator" aria-hidden="true">/</span>
        <a class="footer-link" href="${this.getContactMailtoHref()}" aria-label="Escribir a contacto arroba bichitos punto life">
          ${this.getVisibleEmailLabel()}
        </a>
      </footer>
    `;
  }

  private attachChildren(): void {
    if (!this.shadowRoot) {
      return;
    }

    const status = this.shadowRoot.querySelector<MvAnimaliaDetailStatus>('mv-animalia-detail-status');

    if (status) {
      if (this.viewState === 'loading') {
        status.setStatus(
          'loading',
          'Preparando la ficha editorial',
          'Estamos recuperando el detalle por slug para componer la lectura completa de la especie.',
          false,
          this.getBackHref(),
        );
      }

      if (this.viewState === 'not-found') {
        status.setStatus(
          'not-found',
          'No encontramos esta ficha',
          'El slug solicitado no corresponde a un documento publico disponible en Animalia.',
          false,
          this.getBackHref(),
        );
      }

      if (this.viewState === 'error') {
        status.setStatus(
          'error',
          'No pudimos recuperar la ficha',
          this.errorMessage || 'Prueba de nuevo en unos instantes para volver a cargar este detalle.',
          true,
          this.getBackHref(),
        );
      }

      return;
    }

    if (!this.detail) {
      return;
    }

    const featuredReferenceCard = this.detail.hero.pills.find((pill) => pill.filterKey === 'species')
      ?? this.detail.taxonomy.find((step) => step.isFinal)
      ?? null;
    const taxonomySteps = this.detail.taxonomy.filter((step) => !step.isFinal);

    const hero = this.shadowRoot.querySelector<MvAnimaliaDetailHero>('mv-animalia-detail-hero');
    const identity = this.shadowRoot.querySelector<MvAnimaliaDetailFields>(
      'mv-animalia-detail-fields[data-section="identity"]',
    );
    const references = this.shadowRoot.querySelector<MvAnimaliaDetailFields>(
      'mv-animalia-detail-fields[data-section="references"]',
    );
    const taxonomy = this.shadowRoot.querySelector<MvAnimaliaDetailTaxonomy>('mv-animalia-detail-taxonomy');
    const narrative = this.shadowRoot.querySelector<MvAnimaliaDetailNarrative>('mv-animalia-detail-narrative');

    hero?.setHero(this.detail.hero);
    hero?.setBackHref(this.getBackHref());
    identity?.setSection('Identidad', 'Datos principales', this.detail.identity);
    references?.setSection('Referencias', 'Referencias', this.detail.references, featuredReferenceCard);
    taxonomy?.setSteps(taxonomySteps);
    narrative?.setBlocks(this.detail.narrativeBlocks);
  }

  private getActiveFiltersFromLocation() {
    return animaliaListFiltersFromQuery(getCurrentLocation().query);
  }

  private getBackHref(): string {
    const returnTo = getCurrentLocation().query.get('returnTo');

    if (typeof returnTo === 'string' && returnTo.trim() !== '') {
      return returnTo;
    }

    return buildRoute('/', this.getBackQueryRecord());
  }

  private getBackQueryRecord(): Readonly<Record<string, string>> {
    const search = getCurrentLocation().query.get('search');

    if (typeof search === 'string' && search.trim() !== '') {
      return {
        search,
      };
    }

    return animaliaListFiltersToQueryRecord(this.getActiveFiltersFromLocation());
  }

  private getContactMailtoHref(): string {
    const mailbox = ['con', 'tacto'].join('');
    const host = ['bichitos', 'life'].join('.');

    return `mailto:${mailbox}@${host}`;
  }

  private getVisibleEmailLabel(): string {
    const mailbox = ['con', 'tacto'].join('');
    const domainParts = ['bichitos', 'life'];

    return `${mailbox}@${domainParts[0]}.${domainParts[1]}`;
  }

  private isInitialLoadingState(): boolean {
    return this.viewState === 'loading' && this.detail === null;
  }

  private renderInitialLoadingPlaceholder(): string {
    return `
      <section class="initial-loading" data-loading-placeholder="detail" aria-hidden="true">
        <div class="initial-loading-block"></div>
        <div class="initial-loading-layout">
          <div class="initial-loading-column">
            <div class="initial-loading-card"></div>
            <div class="initial-loading-card"></div>
          </div>
          <div class="initial-loading-column">
            <div class="initial-loading-card"></div>
          </div>
        </div>
        <div class="initial-loading-card"></div>
      </section>
    `;
  }

  private shouldHideIndexableContent(): boolean {
    return this.isInitialLoadingState();
  }

  private applySeo(): void {
    if (this.shouldHideIndexableContent()) {
      return;
    }

    const canonicalPath = this.slug.trim() !== ''
      ? `/animalia/${encodeURIComponent(this.slug)}`
      : '/animalia';

    if (this.viewState === 'not-found') {
      applySeoMetadata({
        title: 'Ficha no encontrada',
        description: 'La ficha solicitada no esta disponible en Bichitos.life. Explora otras especies y taxonomias desde el archivo editorial.',
        path: canonicalPath,
        canonicalUrl: buildSiteUrl(canonicalPath),
        robots: 'noindex,follow',
        jsonLd: createWebsiteJsonLd(),
      });
      return;
    }

    if (this.viewState === 'error') {
      applySeoMetadata({
        title: 'Error al cargar la ficha',
        description: 'No se pudo recuperar esta ficha editorial en Bichitos.life. Puedes volver al listado y seguir explorando otras especies.',
        path: canonicalPath,
        canonicalUrl: buildSiteUrl(canonicalPath),
        robots: 'noindex,follow',
        jsonLd: createWebsiteJsonLd(),
      });
      return;
    }

    if (!this.detail) {
      return;
    }

    const firstNarrativeBlock = this.detail.narrativeBlocks[0] ?? null;
    const taxonomyTrail = this.detail.taxonomy.map((step) => step.value).join(' > ');
    const description = firstNarrativeBlock?.body
      ?? `Ficha editorial de ${this.detail.hero.title}${this.detail.hero.subtitle ? `, tambien conocida como ${this.detail.hero.subtitle}` : ''}, con taxonomia navegable y referencias naturalistas en Bichitos.life.`;

    applySeoMetadata({
      title: this.detail.hero.subtitle
        ? `${this.detail.hero.title} (${this.detail.hero.subtitle})`
        : this.detail.hero.title,
      description,
      path: canonicalPath,
      canonicalUrl: buildSiteUrl(canonicalPath),
      imageUrl: this.detail.hero.imageSrc,
      imageAlt: this.detail.hero.imageAlt,
      ogType: 'article',
      jsonLd: [
        createWebsiteJsonLd(),
        {
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: this.detail.hero.title,
          alternativeHeadline: this.detail.hero.subtitle,
          description,
          url: buildSiteUrl(canonicalPath),
          inLanguage: 'es',
          image: this.detail.hero.imageSrc ? [buildSiteUrl(this.detail.hero.imageSrc)] : undefined,
          about: taxonomyTrail || this.detail.hero.title,
        },
        {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: 'Bichitos.life',
              item: buildSiteUrl('/'),
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: this.detail.hero.title,
              item: buildSiteUrl(canonicalPath),
            },
          ],
        },
      ],
    });
  }
}

if (!customElements.get('mv-animalia-detail-page')) {
  customElements.define('mv-animalia-detail-page', MvAnimaliaDetailPage);
}
